/**
 * useInboxRealtime — subscribes to the human-agent-service Socket.IO channel
 * and appends live messages to the active thread.
 *
 * Security: token is sent in the Socket.IO `auth` object — never in query params.
 *
 * Polling runs only when `VITE_REALTIME_DISABLED=true` (or socket auth fails), and
 * updates the thread silently without loading spinners.
 */
import { useCallback, useEffect, useRef } from "react";
import { io as connectSocket } from "socket.io-client";
import { getConversation } from "@/api/conversationsApi";
import { getStoredAccessToken } from "@/lib/authSession";
import { surfaceInboxHttpError } from "@/lib/inboxErrorToast";
import { hmacSha256Hex } from "@/lib/inboxSocketAuth";

const POLL_INTERVAL_MS = 15000;
const REALTIME_DISABLED =
  typeof import.meta !== "undefined" &&
  (import.meta.env?.VITE_REALTIME_DISABLED === "true" || import.meta.env?.VITE_REALTIME_DISABLED === "1");

function mapRealtimeUserMessage(data) {
  const body = data.message ?? data.message_content ?? "";
  const trimmed = String(body).trim();
  if (!trimmed && !(Array.isArray(data.attachments) && data.attachments.length)) return null;
  const now = new Date();
  const rawMessageId = String(data.message_id ?? "").trim();
  const messageId = rawMessageId
    ? rawMessageId.startsWith("cm-")
      ? rawMessageId
      : `cm-${rawMessageId}`
    : `rt-u-${Date.now()}`;
  return {
    id: messageId,
    sessionId: data.session_id ?? null,
    sender: "user",
    text: trimmed || "[Attachment]",
    time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    type: "text",
    sentAt: data.created_at
      ? new Date(Number(data.created_at)).toISOString()
      : now.toISOString(),
    ephemeral: true,
    attachments: data.attachments ?? [],
  };
}

/**
 * @param {{
 *   conversationId: string | null,
 *   workspaceId?: string | null,
 *   centralInbox?: boolean,
 *   sessionWorkspaceId?: string | null,
 *   onMessage: (msg: object) => void,
 *   onMessageDeleted?: (messageId: string | number) => void,
 *   onThreadSync?: (conversation: object) => void,
 *   onRefresh?: (options: {silent: boolean, force: boolean}) => Promise<void>,
 *   onResolved?: () => void,
 *   onReopened?: (data: object) => void,
 * }} params
 */
export function useInboxRealtime({
  conversationId,
  workspaceId,
  centralInbox = false,
  sessionWorkspaceId = null,
  onMessage,
  onMessageDeleted,
  onThreadSync,
  onRefresh,
  onResolved,
  onReopened,
}) {
  const socketRef = useRef(null);
  const pollRef = useRef(null);
  const joinedSessionRef = useRef(null);
  const authVerifiedRef = useRef(false);
  const syncAbortRef = useRef(null);
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const handlersRef = useRef({});
  handlersRef.current = {
    onMessage,
    onMessageDeleted,
    onThreadSync,
    onRefresh,
    onResolved,
    onReopened,
  };
  const scopeRef = useRef({});
  scopeRef.current = { workspaceId, centralInbox, sessionWorkspaceId };

  const syncThread = useCallback(async () => {
    const requestedId = conversationIdRef.current;
    if (!requestedId) return;
    try {
      if (handlersRef.current.onRefresh) {
        await handlersRef.current.onRefresh({ silent: true, force: true });
        return;
      }
      syncAbortRef.current?.abort();
      const controller = new AbortController();
      syncAbortRef.current = controller;
      const { workspaceId: requestedWorkspaceId, centralInbox: central, sessionWorkspaceId: hint } =
        scopeRef.current;
      const { data } = await getConversation({
        id: requestedId,
        workspaceId: requestedWorkspaceId,
        centralInbox: central,
        sessionWorkspaceId: hint,
        signal: controller.signal,
      });
      if (!data || conversationIdRef.current !== requestedId) return;
      if (handlersRef.current.onThreadSync) {
        handlersRef.current.onThreadSync(data);
      } else if (handlersRef.current.onResolved) {
        handlersRef.current.onResolved();
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        surfaceInboxHttpError(e, { skipAuthRedirect: true });
      }
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(() => {
      // Skip while the tab is hidden — a visibility handler will catch up on return
      if (!document.hidden) void syncThread();
    }, POLL_INTERVAL_MS);
  }, [syncThread, stopPolling]);

  // Sync immediately whenever the agent switches back to this tab
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) void syncThread();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [syncThread]);

  useEffect(() => {
    if (REALTIME_DISABLED) {
      void syncThread();
      startPolling();
      return () => {
        syncAbortRef.current?.abort();
        stopPolling();
      };
    }

    const accessToken = getStoredAccessToken();
    if (!accessToken) {
      void syncThread();
      startPolling();
      return () => {
        syncAbortRef.current?.abort();
        stopPolling();
      };
    }

    const socket = connectSocket(window.location.origin, {
      path: "/socket.io/",
      transports: ["websocket", "polling"],
      auth: { token: accessToken },
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });
    socketRef.current = socket;

    socket.on("auth_challenge", async (payload) => {
      const nonce = payload?.nonce;
      if (!nonce) return;
      try {
        const proof = await hmacSha256Hex(accessToken, nonce);
        if (!proof) {
          if (!pollRef.current) startPolling();
          return;
        }
        socket.emit("auth_challenge_response", { nonce, proof });
      } catch {
        if (!pollRef.current) startPolling();
      }
    });

    socket.on("auth_verified", () => {
      authVerifiedRef.current = true;
      stopPolling();
      const currentSession = conversationIdRef.current;
      if (currentSession) {
        socket.emit("join_session", { session_id: currentSession });
        joinedSessionRef.current = currentSession;
      }
    });

    socket.on("human_agent_message", (data) => {
      if (!data || data.session_id !== conversationIdRef.current) return;
      const body = data.message ?? data.message_content ?? "";
      const trimmed = String(body).trim();
      if (!trimmed && !(Array.isArray(data.attachments) && data.attachments.length)) return;
      const now = new Date();
      const mt = String(data.message_type ?? "chat").toLowerCase();
      handlersRef.current.onMessage?.({
        id: data.message_id ? `hm-${data.message_id}` : `rt-${Date.now()}`,
        sessionId: data.session_id ?? null,
        sender: mt === "note" ? "note" : "agent",
        text: trimmed || "[Attachment]",
        time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        type: "text",
        sentAt: data.created_at
          ? new Date(Number(data.created_at)).toISOString()
          : now.toISOString(),
        ephemeral: true,
        attachments: data.attachments ?? [],
      });
    });

    socket.on("human_agent_message_deleted", (data) => {
      if (!data || data.session_id !== conversationIdRef.current) return;
      handlersRef.current.onMessageDeleted?.(data.message_id);
      void syncThread();
    });

    socket.on("user_message", (data) => {
      if (!data || data.session_id !== conversationIdRef.current) return;
      const msg = mapRealtimeUserMessage(data);
      if (msg) handlersRef.current.onMessage?.(msg);
    });

    socket.on("session_resolved", (data) => {
      if (!data || data.session_id !== conversationIdRef.current) return;
      if (handlersRef.current.onThreadSync || handlersRef.current.onRefresh) {
        void syncThread();
      } else if (handlersRef.current.onResolved) {
        handlersRef.current.onResolved();
      }
    });

    socket.on("session_reopened", (data) => {
      if (!data || data.session_id !== conversationIdRef.current) return;
      handlersRef.current.onReopened?.(data);
      void syncThread();
    });

    socket.on("connect_error", () => {
      if (!pollRef.current) startPolling();
    });

    return () => {
      const joinedSession = joinedSessionRef.current;
      if (joinedSession && authVerifiedRef.current) {
        socket.emit("leave_session", { session_id: joinedSession });
      }
      authVerifiedRef.current = false;
      joinedSessionRef.current = null;
      socket.disconnect();
      socketRef.current = null;
      syncAbortRef.current?.abort();
      stopPolling();
    };
  }, [startPolling, stopPolling, syncThread]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !authVerifiedRef.current) {
      if (conversationId) void syncThread();
      return;
    }

    const previousSession = joinedSessionRef.current;
    if (previousSession && previousSession !== conversationId) {
      socket.emit("leave_session", { session_id: previousSession });
      joinedSessionRef.current = null;
    }
    if (conversationId && previousSession !== conversationId) {
      socket.emit("join_session", { session_id: conversationId });
      joinedSessionRef.current = conversationId;
    }
  }, [conversationId, syncThread]);
}
